import { cloneElement } from 'react';
import { Box, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { Timeline } from '../../../components/History/TimeLine';
import { TimelineIcon } from '../../../components/History/TimeLineIcon';
import { LogAuditoriaEntry, TIPO_EVENTO_CONFIG, TONE_STYLES } from './LogAuditoriaEntry';
import { useLogAuditoria } from '../hooks/useLogAuditoria';
import { TipoEventoAuditoria } from '../types/siniestros.types';

const TIPO_EVENTO_OPTIONS: { value: TipoEventoAuditoria | ''; label: string }[] = [
    { value: '', label: 'Todos los eventos' },
    { value: 'INGRESO_SAS', label: 'Ingreso SAS' },
    { value: 'CAMBIO_ESTADO', label: 'Cambio de estado' },
    { value: 'ASIGNACION_PREVENTOR', label: 'Asignación preventor' },
    { value: 'REGISTRO_AUTORIZACION_UERT', label: 'Autorización UERT' },
    { value: 'INVESTIGACION_PRESENTADA', label: 'Investigación presentada' },
    { value: 'CARGA_DIGITAL_GUARDADA', label: 'Carga digital' },
    { value: 'MAIL_ENVIADO', label: 'Mail enviado' },
    { value: 'EXPORTACION_SRT', label: 'Exportación SRT' },
    { value: 'CIERRE_ADMINISTRATIVO', label: 'Cierre administrativo' },
    { value: 'PRAM_ACTIVADO', label: 'PRAM activado' },
    { value: 'PRAM_DDJJ_CARGADA', label: 'DDJJ PRAM' },
    { value: 'INCUMPLIMIENTO_REGISTRADO', label: 'Incumplimiento' },
    { value: 'SINCRONIZACION_VISITAS', label: 'Sincronización visitas' }
];

interface Props {
    idSiniestroRoam: number | undefined;
}

export function LogAuditoriaSection({ idSiniestroRoam }: Props) {
    const { entradas, entradasFiltradas, filtros, setFiltro, expandedIds, toggleExpanded, isLoading, error } =
        useLogAuditoria(idSiniestroRoam);

    const actorOptions = Array.from(new Set(entradas.map((entrada) => entrada.actor))).sort();

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    select
                    label="Tipo de evento"
                    size="small"
                    value={filtros.tipoEvento ?? ''}
                    onChange={(e) =>
                        setFiltro('tipoEvento', e.target.value as TipoEventoAuditoria | '')
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                >
                    {TIPO_EVENTO_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    select
                    label="Actor"
                    size="small"
                    value={filtros.actor ?? ''}
                    onChange={(e) => setFiltro('actor', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">Todos los actores</MenuItem>
                    {actorOptions.map((actor) => (
                        <MenuItem key={actor} value={actor}>
                            {actor}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {!isLoading && error && (
                <Typography variant="body2" sx={{ color: '#B42318', py: 1 }}>
                    Error al cargar el log de auditoría.
                </Typography>
            )}

            {!isLoading && !error && entradasFiltradas.length === 0 && (
                <Typography variant="body2" sx={{ color: '#667085', py: 1 }}>
                    No hay eventos registrados para este siniestro.
                </Typography>
            )}

            {!isLoading && !error && entradasFiltradas.length > 0 && (
                <Timeline>
                    {entradasFiltradas.map((entrada) => {
                        const config = TIPO_EVENTO_CONFIG[entrada.tipoEvento];
                        const tone = TONE_STYLES[config?.tone ?? 'neutral'];
                        return (
                            <Box display="flex" key={entrada.id}>
                                <TimelineIcon
                                    borderColor={tone.ring}
                                    backgroundColor="#FFFFFF"
                                    icon={
                                        config?.icon
                                            ? cloneElement(config.icon, { sx: { color: tone.color } })
                                            : null
                                    }
                                />
                                <Box flex={1} pl={2} pb={4}>
                                    <LogAuditoriaEntry
                                        entrada={entrada}
                                        expanded={expandedIds.has(entrada.id)}
                                        onToggle={() => toggleExpanded(entrada.id)}
                                    />
                                </Box>
                            </Box>
                        );
                    })}
                </Timeline>
            )}
        </Box>
    );
}
